import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CreditCard,
  User,
  Shield,
  Users,
  Calendar,
  Sparkles,
  Mail,
  ArrowRight,
  LogOut,
  Building2,
  Briefcase,
  MapPin,
  Clock,
  ExternalLink,
  CheckCircle2,
  FileDown,
  Lock,
  Eye,
  EyeOff,
  Phone,
  Linkedin,
  MessageSquare,
  AlertCircle,
  HelpCircle,
  Camera,
  RefreshCw,
  Wallet,
  Utensils,
  Coffee,
  Wine,
  Gift,
  Ticket,
  Award,
  Mic,
  Upload,
  Link2,
  Plus,
  X,
  Check,
  FileText,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react';
import {
  getParticipantProfile,
  updateParticipantProfile,
  getParticipantConnections,
  saveConnectionNote,
  getGoogleWalletPass,
  fetchParticipantPerks,
  fetchMeetings,
  fetchPublicSpeakers,
  requestSpeakerDeckUploadUrl,
  updateSpeakerMaterials,
  getPortalInvoiceDownloadUrl,
  toggleBookmarkedSession,
  getPersonalScheduleIcsUrl,
  requestPortalAccountDeletion,
  ParticipantPerk,
  ParticipantPortalResponse,
  PortalAttendee,
  PortalExhibitor,
  Speaker,
} from '../lib/api';
import {
  isNativeMobileShell,
  setSimulatedNativeMobileShell,
} from '../lib/nativeMobile';
import { buildDynamicAgendaSessions, AgendaSession } from '../data';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { ScheduleXMeetingCalendar } from '../components/meetings/ScheduleXMeetingCalendar';
import { RequestMeetingModal } from '../components/meetings/RequestMeetingModal';
import { MeetingListTab } from '../components/meetings/MeetingListTab';
import { PortalAttendeeDirectory } from '../components/portal/PortalAttendeeDirectory';
import { PortalExhibitorDirectory } from '../components/portal/PortalExhibitorDirectory';

type TabKey = 'card' | 'perks' | 'profile' | 'privacy' | 'connections' | 'meetings' | 'speaker' | 'roles';

const AV_REQUIREMENT_OPTIONS = [
  'Lapel / Collar Mic',
  'Handheld Wireless Mic',
  'HDMI Slide Clicker',
  'Embedded Video with Audio',
  'Confidence Monitor / Teleprompter',
  'Live Demo Internet Drop',
];

export const MyIiabPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Authentication State
  const [userEmail, setUserEmail] = useState<string>('');
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [portalToken, setPortalToken] = useState<string | undefined>(undefined);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Portal Data State
  const [activeTab, setActiveTab] = useState<TabKey>('card');
  const [portalData, setPortalData] = useState<ParticipantPortalResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountResult, setDeleteAccountResult] = useState<string | null>(null);
  const [nativeShellActive, setNativeShellActive] = useState<boolean>(() => isNativeMobileShell());

  // Profile Form State
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    jobTitle: '',
    companyName: '',
    bio: '',
    linkedinUrl: '',
    avatarUrl: '',
    lookingFor: [] as string[],
    offering: [] as string[],
    isDirectoryVisible: true,
    directoryOptOut: false,
    shareContactOptOut: false,
  });
  const [lookingForInput, setLookingForInput] = useState('');
  const [offeringInput, setOfferingInput] = useState('');

  // Connections State
  const [connections, setConnections] = useState<any[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [activeNotes, setActiveNotes] = useState<Record<string, string>>({});

  // Wallet State
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletSuccess, setWalletSuccess] = useState(false);

  // Perks State (Phase 7)
  const [userPerks, setUserPerks] = useState<ParticipantPerk[]>([]);
  const [perksLoading, setPerksLoading] = useState(false);

  // B2B Meetings, Conference Bookmarks & Directories State
  const [meetingsSubTab, setMeetingsSubTab] = useState<'schedule' | 'agenda' | 'exhibitors' | 'delegates'>('schedule');
  const [calendarViewMode, setCalendarViewMode] = useState<'calendar' | 'list'>('calendar');
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [targetUserForMeeting, setTargetUserForMeeting] = useState<any>(null);
  const [slotClickDateTime, setSlotClickDateTime] = useState<string | undefined>(undefined);
  const [bookmarkedSessionIds, setBookmarkedSessionIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('iiab_bookmarked_sessions');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Speaker Hub State (Decisions D5 & D11 + Step 3 Headshot Upload)
  const [headshotUrl, setHeadshotUrl] = useState('');
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [deckUrl, setDeckUrl] = useState('');
  const [deckFileName, setDeckFileName] = useState('');
  const [cloudDeckLink, setCloudDeckLink] = useState('');
  const [avRequirements, setAvRequirements] = useState<string[]>(['Lapel / Collar Mic', 'HDMI Slide Clicker']);
  const [speakerNotes, setSpeakerNotes] = useState('');
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [savingSpeakerMaterials, setSavingSpeakerMaterials] = useState(false);
  const [speakerSaveSuccess, setSpeakerSaveSuccess] = useState<string | null>(null);
  const [speakerSaveError, setSpeakerSaveError] = useState<string | null>(null);

  // Query Public Speakers for Dynamic 2-Day Agenda
  const { data: publicSpeakers = [] } = useQuery<Speaker[]>({
    queryKey: ['public-speakers'],
    queryFn: () => fetchPublicSpeakers(),
    staleTime: 60000,
  });
  const agendaSessions = React.useMemo(() => buildDynamicAgendaSessions(publicSpeakers), [publicSpeakers]);

  // Query B2B Meetings for this attendee
  const { data: meetingsData } = useQuery({
    queryKey: ['meetings', portalData?.attendee?.id],
    queryFn: () => fetchMeetings(portalData!.attendee.id),
    enabled: !!portalData?.attendee?.id,
  });
  const meetings = meetingsData?.meetings || [];

  // 1. Initial Authentication Detection & Seamless Auto-Sign-In (Decision D3)
  useEffect(() => {
    const rawTab = searchParams.get('tab');
    if (rawTab === 'agenda') {
      setActiveTab('meetings');
      setMeetingsSubTab('agenda');
    } else if (
      rawTab &&
      ['card', 'perks', 'profile', 'privacy', 'connections', 'meetings', 'speaker'].includes(rawTab)
    ) {
      setActiveTab(rawTab as TabKey);
    }

    async function initAuth() {
      const queryEmail = searchParams.get('email');
      const tokenParam = searchParams.get('token');
      const savedToken = sessionStorage.getItem('iiab_portal_token');
      const savedEmail =
        sessionStorage.getItem('iiab_portal_email') ||
        sessionStorage.getItem('iiab_participant_email');

      const effectiveToken = tokenParam || savedToken || undefined;
      const effectiveEmail = queryEmail || savedEmail || undefined;

      if (tokenParam) {
        sessionStorage.setItem('iiab_portal_token', tokenParam);
        setPortalToken(tokenParam);
      } else if (savedToken) {
        setPortalToken(savedToken);
      }

      if (queryEmail) {
        sessionStorage.setItem('iiab_portal_email', queryEmail.trim().toLowerCase());
        sessionStorage.setItem('iiab_participant_email', queryEmail.trim().toLowerCase());
      }

      if (effectiveToken || effectiveEmail) {
        if (effectiveEmail) setSessionEmail(effectiveEmail);
        await loadProfile(effectiveEmail, effectiveToken);
        return;
      }

      // Check Supabase session
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user?.email) {
          setSessionEmail(data.session.user.email);
          await loadProfile(data.session.user.email);
          return;
        }
      }
    }

    initAuth();
  }, [searchParams]);

  // 2. Fetch Profile from Backend
  async function loadProfile(email?: string, tokenOverride?: string) {
    setLoading(true);
    setErrorMessage(null);
    try {
      const activeTok = tokenOverride || portalToken;
      const data = await getParticipantProfile(email || undefined, activeTok);
      setPortalData(data);
      const resolvedEmail = data.attendee.email || email || '';
      if (resolvedEmail) {
        setSessionEmail(resolvedEmail);
        sessionStorage.setItem('iiab_participant_email', resolvedEmail);
        sessionStorage.setItem('iiab_portal_email', resolvedEmail);
      }
      setProfileForm({
        fullName: data.attendee.fullName || '',
        jobTitle: data.attendee.jobTitle || '',
        companyName: data.attendee.companyName || '',
        bio: data.attendee.bio || '',
        linkedinUrl: data.attendee.linkedinUrl || '',
        avatarUrl: data.attendee.avatarUrl || '',
        lookingFor: data.attendee.lookingFor || [],
        offering: data.attendee.offering || [],
        isDirectoryVisible: data.attendee.isDirectoryVisible !== false,
        directoryOptOut: data.attendee.directoryOptOut || false,
        shareContactOptOut: data.attendee.shareContactOptOut || false,
      });

      // Initialize Bookmarked Sessions if present
      const serverBookmarks = (data.attendee as any)?.bookmarkedSessionIds;
      if (Array.isArray(serverBookmarks)) {
        setBookmarkedSessionIds(serverBookmarks);
        try {
          localStorage.setItem('iiab_bookmarked_sessions', JSON.stringify(serverBookmarks));
        } catch {
          // ignore
        }
      }

      // Initialize Speaker Hub materials if present
      const spSession = (data.eventCard as any)?.speakerSession;
      if (data.attendee.avatarUrl) {
        setHeadshotUrl(data.attendee.avatarUrl);
      }
      if (spSession) {
        if (spSession.headshotUrl) {
          setHeadshotUrl(spSession.headshotUrl);
        }
        if (spSession.deckUrl) {
          setDeckUrl(spSession.deckUrl);
          setCloudDeckLink(spSession.deckUrl);
        }
        if (spSession.deckFileName) setDeckFileName(spSession.deckFileName);
        if (Array.isArray(spSession.avRequirements) && spSession.avRequirements.length > 0) {
          setAvRequirements(spSession.avRequirements);
        }
        if (spSession.speakerNotes) setSpeakerNotes(spSession.speakerNotes);
      }

      // Load Perks
      loadPerks(data.attendee.id);
    } catch (err: any) {
      setErrorMessage(err.message || 'Unable to find an attendee account with this email or token.');
    } finally {
      setLoading(false);
    }
  }

  // Fetch Participant Perks
  async function loadPerks(attendeeId: string) {
    setPerksLoading(true);
    try {
      const res = await fetchParticipantPerks(attendeeId);
      setUserPerks(res.perks || []);
    } catch {
      // Non-blocking perks load
    } finally {
      setPerksLoading(false);
    }
  }

  // 3. Load Connections
  async function loadConnections(email: string) {
    setConnectionsLoading(true);
    try {
      const res = await getParticipantConnections(email);
      setConnections(res.connections || []);
      const notesMap: Record<string, string> = {};
      for (const c of res.connections || []) {
        if (c.note) notesMap[c.partnerId] = c.note;
      }
      setActiveNotes(notesMap);
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setConnectionsLoading(false);
    }
  }

  // 4. Handle Supabase Magic Link Login
  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!userEmail || !userEmail.includes('@')) {
      setAuthError('Please enter a valid email address.');
      return;
    }

    setAuthLoading(true);
    setAuthError(null);

    try {
      if (supabase && isSupabaseConfigured) {
        const { error } = await supabase.auth.signInWithOtp({
          email: userEmail.trim().toLowerCase(),
          options: {
            emailRedirectTo: `${window.location.origin}/my-iiab`,
          },
        });
        if (error) throw error;
        setIsOtpSent(true);
      } else {
        // Hermetic Dev / Offline Fallback: login directly
        const normalized = userEmail.trim().toLowerCase();
        sessionStorage.setItem('iiab_participant_email', normalized);
        sessionStorage.setItem('iiab_portal_email', normalized);
        setSessionEmail(normalized);
        loadProfile(normalized);
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to dispatch magic link. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  // 5. Handle OTP Code Verification
  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) {
      setAuthError('Please enter the 6-digit code sent to your email.');
      return;
    }

    setAuthLoading(true);
    setAuthError(null);

    try {
      if (supabase) {
        const { data, error } = await supabase.auth.verifyOtp({
          email: userEmail.trim().toLowerCase(),
          token: otpCode.trim(),
          type: 'email',
        });
        if (error) throw error;
        if (data.session?.user?.email) {
          setSessionEmail(data.session.user.email);
          loadProfile(data.session.user.email);
        }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Invalid or expired 6-digit code.');
    } finally {
      setAuthLoading(false);
    }
  }

  // 6. Sign Out
  async function handleSignOut() {
    if (supabase) {
      await supabase.auth.signOut();
    }
    sessionStorage.removeItem('iiab_participant_email');
    sessionStorage.removeItem('iiab_portal_email');
    sessionStorage.removeItem('iiab_portal_token');
    setSessionEmail(null);
    setPortalToken(undefined);
    setPortalData(null);
    setIsOtpSent(false);
    setOtpCode('');
  }

  // 7. Save Profile Changes
  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionEmail && !portalToken) return;

    setSaving(true);
    setSaveSuccess(false);
    try {
      await updateParticipantProfile(profileForm, sessionEmail || undefined, portalToken);
      setSaveSuccess(true);
      loadProfile(sessionEmail || undefined, portalToken);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err: any) {
      alert(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  // 7a. Toggle Bookmarked Conference Session
  async function handleToggleBookmark(session: AgendaSession) {
    const isSaved = bookmarkedSessionIds.includes(session.id);
    const nextIds = isSaved
      ? bookmarkedSessionIds.filter((id) => id !== session.id)
      : [...bookmarkedSessionIds, session.id];
    setBookmarkedSessionIds(nextIds);
    try {
      localStorage.setItem('iiab_bookmarked_sessions', JSON.stringify(nextIds));
    } catch {
      // ignore
    }

    try {
      const res = await toggleBookmarkedSession(
        {
          sessionId: session.id,
          action: isSaved ? 'remove' : 'add',
          session: {
            id: session.id,
            title: session.title,
            date: session.date,
            startTime: session.startTime,
            endTime: session.endTime,
            hall: session.hall,
            track: session.track,
            speakerName: session.speaker?.name || session.moderator?.name || 'IIAB 2027 Keynote',
          },
        },
        sessionEmail || undefined,
        portalToken
      );
      if (res.success && Array.isArray(res.bookmarkedSessionIds)) {
        setBookmarkedSessionIds(res.bookmarkedSessionIds);
      }
    } catch {
      // Keep optimistic update
    }
  }

  // 7b. Speaker Headshot Upload & Deck File Upload (Decisions D5 & D11 + Step 3)
  async function handleHeadshotFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setSpeakerSaveError('Headshot photo exceeds 10 MB limit. Please choose a smaller image.');
      return;
    }

    setUploadingHeadshot(true);
    setSpeakerSaveError(null);
    setSpeakerSaveSuccess(null);
    try {
      const res = await requestSpeakerDeckUploadUrl(
        { fileName: file.name, contentType: file.type || 'image/jpeg', purpose: 'headshot' },
        sessionEmail || undefined,
        portalToken
      );

      let finalUrl = res.publicUrl || '';
      if (res.uploadUrl) {
        const putRes = await fetch(res.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'image/jpeg' },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error('Failed to upload headshot photo to storage.');
        }
        if (!finalUrl) {
          finalUrl = res.uploadUrl.split('?')[0];
        }
      }

      setHeadshotUrl(finalUrl);
      setProfileForm((prev) => ({ ...prev, avatarUrl: finalUrl }));

      await updateSpeakerMaterials(
        { headshotUrl: finalUrl },
        sessionEmail || undefined,
        portalToken
      );

      setSpeakerSaveSuccess(`Uploaded speaker headshot "${file.name}" and updated your public profile photo!`);
    } catch (err: any) {
      setSpeakerSaveError(err.message || 'Unable to upload headshot photo. You can also paste an image URL below.');
    } finally {
      setUploadingHeadshot(false);
    }
  }

  async function handleDeckFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setSpeakerSaveError('Slide deck file exceeds 50 MB limit. Please paste a cloud link instead.');
      return;
    }

    setUploadingDeck(true);
    setSpeakerSaveError(null);
    setSpeakerSaveSuccess(null);
    try {
      const res = await requestSpeakerDeckUploadUrl(
        { fileName: file.name, contentType: file.type || 'application/octet-stream', purpose: 'deck' },
        sessionEmail || undefined,
        portalToken
      );

      let finalUrl = res.publicUrl || '';
      if (res.uploadUrl) {
        const putRes = await fetch(res.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error('Failed to upload slide deck file to storage.');
        }
        if (!finalUrl) {
          finalUrl = res.uploadUrl.split('?')[0];
        }
      }

      setDeckUrl(finalUrl);
      setDeckFileName(file.name);

      await updateSpeakerMaterials(
        {
          deckUrl: finalUrl,
          deckFileName: file.name,
          headshotUrl: headshotUrl.trim() || undefined,
          avRequirements,
          speakerNotes: speakerNotes.trim() || undefined,
        },
        sessionEmail || undefined,
        portalToken
      );

      setSpeakerSaveSuccess(`Uploaded "${file.name}" and saved speaker materials!`);
    } catch (err: any) {
      setSpeakerSaveError(err.message || 'Unable to upload slide deck. You can also paste a cloud link below.');
    } finally {
      setUploadingDeck(false);
    }
  }

  async function handleSaveSpeakerMaterials(e: React.FormEvent) {
    e.preventDefault();
    setSavingSpeakerMaterials(true);
    setSpeakerSaveError(null);
    setSpeakerSaveSuccess(null);
    try {
      const effectiveDeckUrl = cloudDeckLink.trim() || deckUrl || undefined;
      await updateSpeakerMaterials(
        {
          deckUrl: effectiveDeckUrl,
          deckFileName: deckFileName || (cloudDeckLink.trim() ? 'Cloud Presentation Link' : undefined),
          headshotUrl: headshotUrl.trim() || undefined,
          avRequirements,
          speakerNotes: speakerNotes.trim() || undefined,
        },
        sessionEmail || undefined,
        portalToken
      );
      if (cloudDeckLink.trim()) {
        setDeckUrl(cloudDeckLink.trim());
      }
      if (headshotUrl.trim()) {
        setProfileForm((prev) => ({ ...prev, avatarUrl: headshotUrl.trim() }));
      }
      setSpeakerSaveSuccess('Speaker headshot, presentation link & AV requirements saved!');
    } catch (err: any) {
      setSpeakerSaveError(err.message || 'Failed to save speaker materials.');
    } finally {
      setSavingSpeakerMaterials(false);
    }
  }

  function toggleAvRequirement(item: string) {
    setAvRequirements((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  }

  // 8. Add to Google Wallet Action
  async function handleAddToGoogleWallet() {
    if (!portalData?.activeCredential?.badgeCode) return;
    setWalletLoading(true);
    try {
      const res = await getGoogleWalletPass(portalData.activeCredential.badgeCode);
      setWalletSuccess(true);
      // In production, opens the Google Wallet pass URL:
      window.open(res.saveUrl, '_blank');
    } catch (err) {
      console.error(err);
      alert('Unable to generate Google Wallet pass at this time.');
    } finally {
      setWalletLoading(false);
    }
  }

  // 9. Export Connections as CSV
  function handleExportCsv() {
    if (!connections || connections.length === 0) return;

    const headers = ['Full Name', 'Company', 'Job Title', 'Role', 'Connected At', 'Email', 'Phone', 'Private Notes'];
    const rows = connections.map((c) => [
      `"${c.fullName || ''}"`,
      `"${c.companyName || ''}"`,
      `"${c.jobTitle || ''}"`,
      `"${c.primaryRole || ''}"`,
      `"${new Date(c.connectedAt).toLocaleString()}"`,
      `"${c.email || 'Contact Protected'}"`,
      `"${c.phone || 'Contact Protected'}"`,
      `"${c.note || ''}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `IIAB2027_My_Connections_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // -------------------------------------------------------------
  // VIEW A: Authentication Screen (Passwordless Magic Link + OTP)
  // -------------------------------------------------------------
  if (!portalData) {
    if (loading) {
      return (
        <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col justify-center items-center px-4 py-12">
          <div className="w-full max-w-md bg-slate-800/90 border border-slate-700/80 rounded-3xl p-8 shadow-2xl text-center space-y-4">
            <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin mx-auto" />
            <h2 className="text-lg font-bold text-white">Opening Your My IIAB Portal...</h2>
            <p className="text-xs text-slate-400">Verifying your pass credentials and loading your workspace.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col justify-center items-center px-4 py-12">
        <div className="w-full max-w-md bg-slate-800/90 border border-slate-700/80 rounded-3xl p-8 shadow-2xl backdrop-blur-md">
          {/* Logo & Header */}
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A3D27] to-emerald-700 flex items-center justify-center p-3 shadow-lg shadow-emerald-950/40 mb-4 border border-emerald-500/20">
              <Sparkles className="w-8 h-8 text-[#FAF5EA]" />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-white">My IIAB Portal</h1>
            <p className="text-sm text-slate-400 mt-1">
              Access your digital event card, two-way networking connections, and expo schedule
            </p>
          </div>

          {authError && (
            <div className="mb-6 p-4 rounded-xl bg-rose-950/60 border border-rose-800 text-rose-200 text-xs flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <span>{authError}</span>
            </div>
          )}

          {errorMessage && (
            <div className="mb-6 p-4 rounded-xl bg-amber-950/60 border border-amber-800 text-amber-200 text-xs flex items-start gap-2.5">
              <HelpCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {!isOtpSent ? (
            <form onSubmit={handleSendMagicLink} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Registered Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
              >
                {authLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>Send Magic Link &amp; Code</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="p-3 bg-emerald-950/40 border border-emerald-800/60 rounded-xl text-emerald-300 text-xs text-center mb-4">
                We sent a login link and a 6-digit code to <strong>{userEmail}</strong>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  6-Digit Verification Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-center text-xl font-mono tracking-widest text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {authLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <span>Sign In to My IIAB</span>}
              </button>

              <button
                type="button"
                onClick={() => setIsOtpSent(false)}
                className="w-full text-xs text-slate-400 hover:text-slate-200 text-center block pt-2"
              >
                ← Back to email input
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-slate-700/60 text-center">
            <p className="text-xs text-slate-500">
              Not registered yet?{' '}
              <a href="/register" className="text-emerald-400 hover:underline">
                Book your pass here
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { attendee, event, eventCard } = portalData;
  const collectionCode = (attendee.collectionCode || attendee.id.slice(0, 6)).toUpperCase();

  // -------------------------------------------------------------
  // VIEW B: Full Participant Portal Console
  // -------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top Banner Navigation */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-30 shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo/iiab_logo.png" alt="IIAB Logo" className="h-8 w-auto object-contain" />
            <div>
              <h2 className="text-sm font-bold tracking-tight text-white flex items-center gap-2">
                <span>My IIAB</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800">
                  {eventCard.theme.label}
                </span>
              </h2>
              <p className="text-xs text-slate-400 hidden sm:block">
                {event.name} · {event.city}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-300 hidden md:inline-block">{attendee.fullName}</span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-medium transition-colors border border-slate-700"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto border-t border-slate-800/80 scrollbar-none">
          <button
            onClick={() => setActiveTab('card')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'card'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            <span>Digital Event Card</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('perks');
              if (portalData) loadPerks(portalData.attendee.id);
            }}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'perks'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Utensils className="w-4 h-4" />
            <span>My Perks &amp; Vouchers</span>
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'profile'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <User className="w-4 h-4" />
            <span>My Profile</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('connections');
              if (sessionEmail) loadConnections(sessionEmail);
            }}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'connections'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>My Connections</span>
          </button>

          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'privacy'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span>Privacy &amp; Sharing</span>
          </button>

          <button
            onClick={() => setActiveTab('meetings')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'meetings'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>B2B Meetings</span>
          </button>

          <button
            onClick={() => setActiveTab('speaker')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'speaker'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Mic className="w-4 h-4" />
            <span>Speaker Hub</span>
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* ========================================================= */}
        {/* TAB 1: Digital Event Card (Informational, NO QR/NFC)      */}
        {/* ========================================================= */}
        {activeTab === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-6 flex flex-col items-center">
              <div className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-[#0A3D27] via-[#062B1B] to-slate-900 p-1 shadow-2xl text-white">
                <div className="bg-slate-900/90 rounded-[22px] p-6 border border-emerald-500/20 flex flex-col items-center relative overflow-hidden">
                  {/* Event Watermark Header */}
                  <div className="w-full flex items-center justify-between pb-4 border-b border-slate-800">
                    <img src="/logo/iiab_logo.png" alt="IIAB Logo" className="h-7 w-auto object-contain" />
                    <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-widest">
                      Digital Event Card
                    </span>
                  </div>

                  {/* Photo & Identity */}
                  <div className="my-5 flex flex-col items-center text-center">
                    <div className="w-24 h-24 rounded-2xl bg-slate-800 border-2 border-emerald-500/40 p-1 mb-3 shadow-md overflow-hidden relative">
                      {eventCard.photoUrl ? (
                        <img
                          src={eventCard.photoUrl}
                          alt={attendee.fullName}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500">
                          <User className="w-10 h-10" />
                        </div>
                      )}
                    </div>

                    <h3 className="text-xl font-black text-white tracking-tight">{attendee.fullName}</h3>
                    {attendee.jobTitle && <p className="text-xs text-slate-300 font-medium">{attendee.jobTitle}</p>}
                    {attendee.companyName && (
                      <p className="text-xs font-semibold text-emerald-400 mt-0.5">{attendee.companyName}</p>
                    )}
                  </div>

                  {/* Primary Role Color Band */}
                  <div className="w-full rounded-xl py-2 px-3 bg-gradient-to-r from-emerald-950 to-slate-900 border border-emerald-800/80 text-center mb-4">
                    <span className="text-xs font-black tracking-widest text-[#FAF5EA] uppercase">
                      {eventCard.theme.label}
                    </span>
                  </div>

                  {/* Prominent 6-Character Desk Collection Code (Strictly NO QR on Digital Card) */}
                  <div className="w-full rounded-2xl bg-slate-950/90 border border-[#E6C687]/40 p-4 text-center mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#E6C687] block mb-1">
                      Desk Collection Code
                    </span>
                    <div className="text-2xl font-mono font-black tracking-[0.28em] text-white select-all">
                      {collectionCode}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">
                      Present this 6-char code + photo ID at Pavilion 3 Desk for your printed 4×6 QR &amp; NFC badge
                    </p>
                  </div>

                  {/* Event Details */}
                  <div className="w-full space-y-2 text-xs text-slate-400 border-t border-slate-800 pt-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span>{eventCard.dates}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span className="line-clamp-1">{eventCard.venue}</span>
                    </div>
                  </div>

                  {/* Collection Notice Badge */}
                  <div className="w-full mt-4 p-3 rounded-xl bg-amber-950/40 border border-amber-800/50 text-amber-300 text-[11px] leading-relaxed text-center">
                    {eventCard.collectionNotice}
                  </div>
                </div>
              </div>

              {/* Add to Google Wallet Button */}
              <div className="mt-6 w-full max-w-sm flex flex-col items-center">
                <button
                  onClick={handleAddToGoogleWallet}
                  disabled={walletLoading}
                  className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white font-semibold py-3 px-5 rounded-2xl border border-slate-700 shadow-md transition-all cursor-pointer"
                >
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  <span>{walletLoading ? 'Generating Pass...' : 'Add to Google Wallet'}</span>
                </button>
                {walletSuccess && (
                  <p className="text-xs text-emerald-600 mt-2 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Google Wallet pass link opened!</span>
                  </p>
                )}
              </div>
            </div>

            <div className="md:col-span-6 space-y-6">
              <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
                <h4 className="text-base font-bold text-slate-900 mb-2">Physical Badge Information</h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Your official 4&quot; × 6&quot; printed physical badge includes your high-resolution photograph, scannable QR
                  code, and tap-to-connect NFC chip. Badges are pre-printed and prepared for quick collection at the
                  Registration Desk in Pavilion 3.
                </p>

                <div className="mt-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Badge Collection Code:</span>
                    <span className="font-mono font-black text-sm px-2.5 py-0.5 rounded-lg bg-[#FAF5EA] text-[#785613] border border-[#E6C687]">
                      {collectionCode}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Badge Status:</span>
                    <span className="font-semibold text-emerald-700 uppercase tracking-wider">
                      {attendee.badgeStatus}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Registered Tier:</span>
                    <span className="font-semibold text-slate-900 capitalize">
                      {attendee.ticketType ? `${attendee.ticketType} Pass` : attendee.tier}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/80 flex justify-between items-center">
                    <span className="text-slate-500">GST Tax Invoice:</span>
                    <a
                      href={getPortalInvoiceDownloadUrl(
                        attendee.id,
                        portalToken || attendee.profileToken || ''
                      )}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-bold text-[11px] transition-colors shadow-2xs"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      <span>
                        {attendee.zohoInvoiceNumber
                          ? `Download #${attendee.zohoInvoiceNumber}`
                          : 'Download GST Invoice'}
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              {eventCard.speakerSession && (
                <div className="bg-white rounded-3xl p-6 border border-rose-100 shadow-sm bg-gradient-to-br from-rose-50/50 to-white">
                  <h4 className="text-base font-bold text-rose-900 mb-2">Your Speaker Session</h4>
                  <p className="text-sm font-semibold text-slate-900">{eventCard.speakerSession.topicTitle}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-rose-600" />
                      {eventCard.speakerSession.sessionDate} at {eventCard.speakerSession.sessionStartTime}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-rose-600" />
                      {eventCard.speakerSession.stageOrHall}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('speaker')}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 hover:text-rose-900 cursor-pointer"
                  >
                    <Mic className="w-3.5 h-3.5" />
                    <span>Upload Slide Deck &amp; AV Requirements →</span>
                  </button>
                </div>
              )}

              {eventCard.exhibitorStall && (
                <div className="bg-white rounded-3xl p-6 border border-sky-100 shadow-sm bg-gradient-to-br from-sky-50/50 to-white">
                  <h4 className="text-base font-bold text-sky-900 mb-2">Exhibitor Stall</h4>
                  <p className="text-sm text-slate-700">
                    Your assigned stall number is{' '}
                    <strong className="font-mono text-sky-800 text-base">{eventCard.exhibitorStall.stallId}</strong>.
                  </p>
                  <a
                    href="/exhibitor-portal"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900"
                  >
                    <span>Open Exhibitor Management Portal</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 2: My Perks & Meal Vouchers (Phase 7)                 */}
        {/* ========================================================= */}
        {activeTab === 'perks' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2.5">
                    <Utensils className="w-5 h-5 text-[#0A3D27]" />
                    <span>My Meal Vouchers &amp; Hospitality Perks</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Present your printed badge QR code or tap your NFC tag at Pavilion 3 dining stations to redeem.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => portalData && loadPerks(portalData.attendee.id)}
                  disabled={perksLoading}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 self-start cursor-pointer transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${perksLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>

              {perksLoading && userPerks.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                  Loading your entitlements...
                </div>
              ) : userPerks.length === 0 ? (
                <div className="py-10 px-6 bg-slate-50 border border-slate-200/80 rounded-2xl text-center space-y-2">
                  <Ticket className="w-8 h-8 text-slate-400 mx-auto" />
                  <h4 className="font-bold text-slate-800 text-sm">No Special Vouchers Allocated</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    Your current pass tier gives you access to exhibition halls and public cafeterias. Conference delegate and VIP passes include complimentary buffet lunches and lounge access.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userPerks.map((p) => {
                    const isMeal = p.code.includes('LUNCH') || p.code.includes('MEAL');
                    const isLounge = p.code.includes('VIP') || p.code.includes('LOUNGE') || p.code.includes('COFFEE');
                    const isGala = p.code.includes('GALA') || p.code.includes('DINNER');
                    const Icon = isMeal ? Utensils : isLounge ? Coffee : isGala ? Wine : Ticket;

                    return (
                      <div
                        key={p.id}
                        className={`border rounded-2xl p-5 space-y-3.5 transition-all ${
                          p.isRedeemed
                            ? 'bg-slate-50 border-slate-200/80'
                            : 'bg-white border-[#E6C687]/80 shadow-xs hover:border-[#0A3D27]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2.5 rounded-xl ${
                                p.isRedeemed
                                  ? 'bg-slate-200 text-slate-500'
                                  : 'bg-[#0A3D27]/10 text-[#0A3D27]'
                              }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-sm text-slate-900 leading-tight">
                                {p.name}
                              </h4>
                              <span className="text-[10px] text-slate-500 block">
                                {p.validDate ? `Valid: ${p.validDate}` : 'Event-Wide Access'}
                              </span>
                            </div>
                          </div>

                          {p.isRedeemed ? (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700 shrink-0">
                              Redeemed
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 shrink-0 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                              Ready to Claim
                            </span>
                          )}
                        </div>

                        {p.description && (
                          <p className="text-xs text-slate-600 line-clamp-2">{p.description}</p>
                        )}

                        {p.sponsorName && (
                          <div className="text-[10px] font-semibold text-[#785613] bg-[#FAF5EA] border border-[#E6C687]/40 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                            <Award className="w-3 h-3 text-[#785613]" />
                            <span>Sponsored by {p.sponsorName}</span>
                          </div>
                        )}

                        {p.isRedeemed && p.lastRedemption && (
                          <div className="text-[10px] text-slate-500 bg-slate-100 rounded-lg p-2 space-y-0.5">
                            <div className="font-semibold text-slate-700">Redeemed at Station</div>
                            <div>
                              {p.lastRedemption.vendorCounterId} ({p.lastRedemption.vendorName}) on{' '}
                              {new Date(p.lastRedemption.redeemedAt).toLocaleDateString()}
                            </div>
                          </div>
                        )}

                        {!p.isRedeemed && (
                          <div className="text-[11px] text-[#0A3D27] font-semibold bg-emerald-50/80 border border-emerald-200/60 rounded-xl px-3 py-2 flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-[#0A3D27] shrink-0" />
                            <span>Scan badge QR at Pavilion 3 dining counters</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 3: Profile, Bio & Matchmaking Tags                    */}
        {/* ========================================================= */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 mb-1">My Participant Profile</h3>
            <p className="text-xs text-slate-500 mb-6">
              This information appears on your profile when delegates scan your badge QR code, tap your NFC tag, or browse the B2B Matchmaking Directory.
            </p>

            {saveSuccess && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Your profile changes have been saved successfully!</span>
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">
                  Name on Badge
                </label>
                <input
                  type="text"
                  required
                  value={profileForm.fullName}
                  onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">Job Title</label>
                  <input
                    type="text"
                    value={profileForm.jobTitle}
                    onChange={(e) => setProfileForm({ ...profileForm, jobTitle: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">Company Name</label>
                  <input
                    type="text"
                    value={profileForm.companyName}
                    onChange={(e) => setProfileForm({ ...profileForm, companyName: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">Photo Avatar URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://..."
                    value={profileForm.avatarUrl}
                    onChange={(e) => setProfileForm({ ...profileForm, avatarUrl: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">LinkedIn Profile</label>
                <input
                  type="url"
                  placeholder="https://linkedin.com/in/..."
                  value={profileForm.linkedinUrl}
                  onChange={(e) => setProfileForm({ ...profileForm, linkedinUrl: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-700 mb-1.5">Bio / Overview</label>
                <textarea
                  rows={3}
                  placeholder="Tell other attendees about your background, technology focus, or investment interests..."
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-600"
                />
              </div>

              {/* Matchmaking Tags: Looking For */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-800">
                    What I&apos;m Looking For (B2B Matchmaking Tags)
                  </label>
                  <p className="text-[11px] text-slate-500">
                    Add keywords like &quot;Bio-Stimulants&quot;, &quot;Drone Telemetry&quot;, &quot;Export Distributors&quot;, or &quot;Series A Funding&quot;.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {profileForm.lookingFor.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-900"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setProfileForm({
                            ...profileForm,
                            lookingFor: profileForm.lookingFor.filter((t) => t !== tag),
                          })
                        }
                        className="text-emerald-700 hover:text-emerald-950 font-bold cursor-pointer"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add tag (e.g., Cold Chain Logistics)"
                    value={lookingForInput}
                    onChange={(e) => setLookingForInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && lookingForInput.trim()) {
                        e.preventDefault();
                        if (!profileForm.lookingFor.includes(lookingForInput.trim())) {
                          setProfileForm({
                            ...profileForm,
                            lookingFor: [...profileForm.lookingFor, lookingForInput.trim()],
                          });
                        }
                        setLookingForInput('');
                      }
                    }}
                    className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (lookingForInput.trim() && !profileForm.lookingFor.includes(lookingForInput.trim())) {
                        setProfileForm({
                          ...profileForm,
                          lookingFor: [...profileForm.lookingFor, lookingForInput.trim()],
                        });
                        setLookingForInput('');
                      }
                    }}
                    className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 cursor-pointer"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Matchmaking Tags: Offering */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-800">
                    What We Offer (Products / Capabilities)
                  </label>
                  <p className="text-[11px] text-slate-500">
                    Highlight solutions or services your organization brings to IIAB 2027.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {profileForm.offering.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FAF5EA] text-[#785613] border border-[#E6C687]"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setProfileForm({
                            ...profileForm,
                            offering: profileForm.offering.filter((t) => t !== tag),
                          })
                        }
                        className="text-[#785613] hover:text-slate-900 font-bold cursor-pointer"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add offering (e.g., IoT Soil Sensors)"
                    value={offeringInput}
                    onChange={(e) => setOfferingInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && offeringInput.trim()) {
                        e.preventDefault();
                        if (!profileForm.offering.includes(offeringInput.trim())) {
                          setProfileForm({
                            ...profileForm,
                            offering: [...profileForm.offering, offeringInput.trim()],
                          });
                        }
                        setOfferingInput('');
                      }
                    }}
                    className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (offeringInput.trim() && !profileForm.offering.includes(offeringInput.trim())) {
                        setProfileForm({
                          ...profileForm,
                          offering: [...profileForm.offering, offeringInput.trim()],
                        });
                        setOfferingInput('');
                      }
                    }}
                    className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 cursor-pointer"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-50"
                >
                  {saving ? 'Saving Changes...' : 'Save Profile Details'}
                </button>
              </div>
            </form>

            {/* Mobile App Shell Preview & Account Deletion Section */}
            <div className="mt-8 pt-6 border-t border-slate-200 space-y-4">
              <div className="p-4 rounded-2xl bg-[#FAF5EA] border border-[#E6C687]/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-bold text-[#785613] uppercase tracking-wider">
                    IIAB 2027 Native Mobile App Shell (iOS &amp; Android)
                  </h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Preview the role-adaptive mobile bottom navigation bar (Delegate, Exhibitor Scanner, and Staff Kiosk modes).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = !nativeShellActive;
                    setSimulatedNativeMobileShell(next);
                    setNativeShellActive(next);
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 cursor-pointer transition-colors ${
                    nativeShellActive
                      ? 'bg-[#0A3D27] text-white'
                      : 'bg-white border border-[#E6C687] text-[#785613] hover:bg-amber-50'
                  }`}
                >
                  {nativeShellActive ? '✓ Mobile App Mode Active' : 'Preview Mobile App Mode'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 4: Privacy & Sharing Controls (§5.3)                  */}
        {/* ========================================================= */}
        {activeTab === 'privacy' && (
          <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 mb-1">Privacy &amp; Contact Sharing</h3>
            <p className="text-xs text-slate-500 mb-6">
              Under India&apos;s Digital Personal Data Protection (DPDP) Act, you have full control over what other delegates
              see when interacting with your credentials.
            </p>

            {saveSuccess && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Your privacy settings have been updated!</span>
              </div>
            )}

            <div className="space-y-6">
              {/* Directory Listing */}
              <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div>
                  <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                    <Eye className="w-4 h-4 text-emerald-600" />
                    <span>Include Me in Attendee Directory</span>
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Allows other registered attendees to search for you by company and industry segment.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={profileForm.isDirectoryVisible && !profileForm.directoryOptOut}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      isDirectoryVisible: e.target.checked,
                      directoryOptOut: !e.target.checked,
                    })
                  }
                  className="w-5 h-5 accent-emerald-600 rounded cursor-pointer mt-1"
                />
              </div>

              {/* Direct Contact Sharing */}
              <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div>
                  <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    <span>Share Phone &amp; Email on Badge Scans</span>
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    When another attendee scans your badge QR or taps your NFC tag, they can reveal and save your
                    business phone number and email address.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!profileForm.shareContactOptOut}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      shareContactOptOut: !e.target.checked,
                    })
                  }
                  className="w-5 h-5 accent-emerald-600 rounded cursor-pointer mt-1"
                />
              </div>

              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-md transition-all cursor-pointer"
                >
                  {saving ? 'Updating Settings...' : 'Save Privacy Preferences'}
                </button>
              </div>

              {/* Apple App Store 5.1.1(v) & Google Play Mandatory In-App Account Deletion Request */}
              <div className="pt-6 border-t border-slate-200">
                <div className="p-5 rounded-2xl bg-rose-50/60 border border-rose-200/80 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-rose-900">
                        Account &amp; Personal Data Deletion (DPDP / App Store Compliance)
                      </h4>
                      <p className="text-xs text-rose-800/80 mt-0.5">
                        Request permanent deletion of your participant profile and B2B networking data. Your profile will be hidden from all public directories immediately.
                      </p>
                    </div>
                    {!showDeleteConfirm && !deleteAccountResult && (
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="px-3 py-1.5 rounded-xl bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 text-xs font-bold shrink-0 cursor-pointer transition-colors"
                      >
                        Request Account Deletion
                      </button>
                    )}
                  </div>

                  {deleteAccountResult && (
                    <div className="p-3 rounded-xl bg-white border border-rose-200 text-xs text-rose-900 font-medium">
                      ✓ {deleteAccountResult}
                    </div>
                  )}

                  {showDeleteConfirm && !deleteAccountResult && (
                    <div className="p-4 rounded-xl bg-white border border-rose-200 space-y-3">
                      <label className="block text-xs font-semibold text-slate-700">
                        Optional reason for account &amp; data erasure:
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., No longer attending IIAB 2027 / Privacy preference"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={deletingAccount}
                          onClick={async () => {
                            setDeletingAccount(true);
                            try {
                              const res = await requestPortalAccountDeletion(
                                { reason: deleteReason },
                                sessionEmail || undefined,
                                portalToken || portalData?.attendee?.profileToken || undefined
                              );
                              setDeleteAccountResult(res.message);
                              setProfileForm((prev) => ({
                                ...prev,
                                isDirectoryVisible: false,
                                directoryOptOut: true,
                                shareContactOptOut: true,
                              }));
                              setShowDeleteConfirm(false);
                            } catch (err: any) {
                              setErrorMessage(err?.message || 'Failed to submit deletion request.');
                            } finally {
                              setDeletingAccount(false);
                            }
                          }}
                          className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                        >
                          {deletingAccount ? 'Submitting Request...' : 'Confirm Account & Data Deletion'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(false)}
                          className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 5: Two-Way My Connections Directory (§5.4)            */}
        {/* ========================================================= */}
        {activeTab === 'connections' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">My Connections ({connections.length})</h3>
                <p className="text-xs text-slate-500">
                  Every mutual badge scan and NFC tap is recorded here like a modern digital business card exchange.
                </p>
              </div>

              {connections.length > 0 && (
                <button
                  onClick={handleExportCsv}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              )}
            </div>

            {connectionsLoading ? (
              <div className="p-12 text-center text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                <p className="text-xs">Loading connections...</p>
              </div>
            ) : connections.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 shadow-sm">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h4 className="text-base font-bold text-slate-800">No connections recorded yet</h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
                  Connect with other delegates at the conference by scanning their badge QR code or tapping their NFC
                  card at Pavilion 3.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {connections.map((c) => (
                  <div
                    key={c.partnerId}
                    className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 shrink-0 overflow-hidden flex items-center justify-center">
                          {c.avatarUrl ? (
                            <img src={c.avatarUrl} alt={c.fullName} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-slate-900 truncate">{c.fullName}</h4>
                          <p className="text-xs text-slate-600 truncate">{c.jobTitle}</p>
                          <p className="text-xs font-semibold text-emerald-700 truncate">{c.companyName}</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                          {c.primaryRole}
                        </span>
                      </div>

                      {/* Contact Details (if shared) */}
                      <div className="mt-4 pt-3 border-t border-slate-100 text-xs space-y-1 text-slate-600">
                        {c.email ? (
                          <div className="flex items-center gap-1.5 truncate">
                            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <a href={`mailto:${c.email}`} className="hover:text-emerald-700 truncate">
                              {c.email}
                            </a>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">Contact details protected by privacy settings</span>
                        )}
                        {c.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <a href={`tel:${c.phone}`} className="hover:text-emerald-700">
                              {c.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Private Note */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        Private Note
                      </label>
                      <input
                        type="text"
                        placeholder="Add discussion notes..."
                        value={activeNotes[c.partnerId] || ''}
                        onChange={(e) => setActiveNotes({ ...activeNotes, [c.partnerId]: e.target.value })}
                        onBlur={() => {
                          if (attendee.id && c.partnerId) {
                            saveConnectionNote(attendee.id, c.partnerId, activeNotes[c.partnerId] || '');
                          }
                        }}
                        className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-600"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 6: Full B2B Meetings, Conference Agenda & Directories */}
        {/* ========================================================= */}
        {activeTab === 'meetings' && (
          <div className="space-y-6">
            {/* Sub-navigation bar for B2B Meetings, Conference Agenda & Directories */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">My 2-Day Schedule &amp; B2B Matchmaking</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Manage your bookmarked conference sessions, 1-on-1 B2B meetings (27–28 July 2027), and export your unified calendar.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={getPersonalScheduleIcsUrl(sessionEmail || undefined, portalToken)}
                  download="IIAB-2027-My-Schedule.ics"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FAF5EA] border border-[#E6C687] text-[#785613] hover:bg-[#F3E9D2] text-xs font-extrabold transition-colors shadow-2xs"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  <span>Download Full Schedule (.ics)</span>
                </a>

                <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => setMeetingsSubTab('schedule')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      meetingsSubTab === 'schedule'
                        ? 'bg-[#0A3D27] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    My Schedule ({meetings.length + bookmarkedSessionIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setMeetingsSubTab('agenda')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                      meetingsSubTab === 'agenda'
                        ? 'bg-[#0A3D27] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Bookmark className="w-3.5 h-3.5" />
                    <span>2-Day Agenda ({bookmarkedSessionIds.length} Saved)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMeetingsSubTab('exhibitors')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                      meetingsSubTab === 'exhibitors'
                        ? 'bg-[#0A3D27] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span>Exhibitor Directory</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMeetingsSubTab('delegates')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                      meetingsSubTab === 'delegates'
                        ? 'bg-[#0A3D27] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>Delegate Directory</span>
                  </button>
                </div>
              </div>
            </div>

            {meetingsSubTab === 'schedule' && (
              <div className="space-y-6">
                {/* Bookmarked Conference Sessions Summary Card */}
                <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                        <BookmarkCheck className="w-4 h-4 text-[#0A3D27]" />
                        <span>Saved Conference Sessions ({bookmarkedSessionIds.length})</span>
                      </h4>
                      <p className="text-xs text-slate-500">
                        Bookmarked keynotes and panels are automatically included in your downloadable .ics calendar file.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMeetingsSubTab('agenda')}
                      className="text-xs font-extrabold text-[#0A3D27] hover:underline self-start sm:self-auto cursor-pointer"
                    >
                      + Browse 2-Day Conference Agenda →
                    </button>
                  </div>

                  {bookmarkedSessionIds.length === 0 ? (
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/70 text-xs text-slate-600 flex items-center justify-between gap-3">
                      <span>You haven&apos;t bookmarked any conference keynotes or technical panels yet.</span>
                      <button
                        type="button"
                        onClick={() => setMeetingsSubTab('agenda')}
                        className="px-3 py-1.5 rounded-xl bg-[#0A3D27] text-white font-bold shrink-0 cursor-pointer"
                      >
                        Explore Sessions
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {agendaSessions
                        .filter((s) => bookmarkedSessionIds.includes(s.id))
                        .map((session) => (
                          <div
                            key={session.id}
                            className="p-3.5 rounded-2xl bg-[#F8F9F6] border border-emerald-200/80 flex items-start justify-between gap-3"
                          >
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] font-extrabold text-white bg-[#0A3D27] px-2 py-0.5 rounded-md">
                                  Day {session.day} • {session.timeSlot}
                                </span>
                                <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] border border-[#E6C687] px-2 py-0.5 rounded-md">
                                  {session.hall}
                                </span>
                              </div>
                              <p className="text-xs font-extrabold text-slate-900 truncate">{session.title}</p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {session.speaker?.name ||
                                  session.moderator?.name ||
                                  session.panelists?.map((sp) => sp.name).join(' • ') ||
                                  'IIAB 2027 Faculty'}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleBookmark(session)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0 cursor-pointer"
                              title="Remove from Saved Schedule"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="inline-flex rounded-xl bg-white border border-slate-200 p-1">
                    <button
                      type="button"
                      onClick={() => setCalendarViewMode('list')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                        calendarViewMode === 'list'
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      List View
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarViewMode('calendar')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                        calendarViewMode === 'calendar'
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      2-Day Calendar Grid
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setMeetingsSubTab('exhibitors')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-semibold shadow-xs cursor-pointer"
                  >
                    <span>+ Request New Meeting</span>
                  </button>
                </div>

                {calendarViewMode === 'calendar' ? (
                  <ScheduleXMeetingCalendar
                    meetings={meetings}
                    onSlotClick={(startDateTime) => {
                      setSlotClickDateTime(startDateTime);
                      setMeetingsSubTab('exhibitors');
                    }}
                  />
                ) : (
                  <MeetingListTab
                    meetings={meetings}
                    currentUserId={attendee.id}
                  />
                )}
              </div>
            )}

            {meetingsSubTab === 'agenda' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {[1, 2].map((dayNum) => {
                  const daySessions = agendaSessions.filter((s) => s.day === dayNum);
                  return (
                    <div key={dayNum} className="space-y-3">
                      <div className="bg-[#0A3D27] text-white rounded-2xl p-4 shadow-xs flex items-center justify-between border border-emerald-800">
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider text-[#E6C687] bg-white/10 px-2 py-0.5 rounded-full">
                            Day {dayNum} • {dayNum === 1 ? 'Tuesday, 27 July 2027' : 'Wednesday, 28 July 2027'}
                          </span>
                          <h4 className="text-xs font-extrabold text-white mt-1">
                            {dayNum === 1 ? 'Policy, Biologicals & AI Precision' : 'Formulations, Cold Chains & Robotics'}
                          </h4>
                        </div>
                        <span className="text-[10px] font-extrabold text-emerald-200 bg-white/10 px-2.5 py-1 rounded-full">
                          {daySessions.length} Sessions
                        </span>
                      </div>

                      <div className="space-y-3">
                        {daySessions.map((session) => {
                          const isSaved = bookmarkedSessionIds.includes(session.id);
                          return (
                            <div
                              key={session.id}
                              className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs space-y-2.5"
                            >
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-[11px] font-extrabold text-[#0A3D27] bg-emerald-50 border border-emerald-200/70 px-2.5 py-0.5 rounded-lg">
                                  {session.timeSlot}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleToggleBookmark(session)}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                                    isSaved
                                      ? 'bg-[#0A3D27] text-white shadow-2xs'
                                      : 'bg-slate-100 hover:bg-emerald-50 text-slate-700 hover:text-[#0A3D27] border border-slate-200'
                                  }`}
                                >
                                  {isSaved ? (
                                    <>
                                      <BookmarkCheck className="w-3.5 h-3.5 text-[#E6C687]" />
                                      <span>Saved</span>
                                    </>
                                  ) : (
                                    <>
                                      <Bookmark className="w-3.5 h-3.5" />
                                      <span>Save</span>
                                    </>
                                  )}
                                </button>
                              </div>
                              <h5 className="text-xs font-black text-slate-900 leading-snug">{session.title}</h5>
                              {session.abstract && (
                                <p className="text-[11px] text-slate-600 line-clamp-2">{session.abstract}</p>
                              )}
                              <div className="flex items-center justify-between text-[10px] text-slate-500 pt-2 border-t border-slate-100">
                                <span className="font-bold text-[#0A3D27]">{session.hall}</span>
                                <span>
                                  {session.speaker?.name ||
                                    session.moderator?.name ||
                                    session.panelists?.map((s) => s.name).join(' • ') ||
                                    'IIAB 2027 Faculty'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {meetingsSubTab === 'exhibitors' && (
              <PortalExhibitorDirectory
                onScheduleMeeting={(ex: PortalExhibitor) => {
                  setTargetUserForMeeting({
                    id: ex.reservationId || (ex as any).id,
                    fullName: ex.fasciaName || ex.companyName,
                    name: ex.fasciaName || ex.companyName,
                    companyName: ex.companyName,
                    org: ex.companyName,
                    jobTitle: `Stall ${ex.boothId}`,
                    tier: 'exhibitor',
                    type: 'exhibitor',
                    email: ex.contactEmail,
                    stallNumber: ex.boothId,
                  });
                  setIsMeetingModalOpen(true);
                }}
              />
            )}

            {meetingsSubTab === 'delegates' && (
              <PortalAttendeeDirectory
                currentUserId={attendee.id}
                onScheduleMeeting={(att: PortalAttendee) => {
                  setTargetUserForMeeting({
                    id: att.id,
                    fullName: att.fullName,
                    name: att.fullName,
                    companyName: att.companyName || '',
                    org: att.companyName || att.jobTitle || 'Delegate',
                    jobTitle: att.jobTitle || '',
                    tier: att.tier,
                    type: 'attendee',
                    email: (att as any).email || 'attendee@iiab-delegate.org',
                  });
                  setIsMeetingModalOpen(true);
                }}
              />
            )}

            {targetUserForMeeting && (
              <RequestMeetingModal
                isOpen={isMeetingModalOpen}
                onClose={() => {
                  setIsMeetingModalOpen(false);
                  setTargetUserForMeeting(null);
                }}
                currentUser={{
                  type: 'attendee',
                  id: attendee.id,
                  name: attendee.fullName,
                  org: attendee.companyName || 'Delegate',
                  email: attendee.email,
                }}
                targetUser={targetUserForMeeting}
                initialDateTime={slotClickDateTime}
              />
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 7: Speaker Hub & Presentation Upload (Decision D5)    */}
        {/* ========================================================= */}
        {activeTab === 'speaker' && (
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Session Assignment Banner */}
            <div className="bg-gradient-to-br from-[#0A3D27] via-[#062B1B] to-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl border border-emerald-500/20">
              <div className="flex items-center gap-2 text-[#E6C687] text-xs font-bold uppercase tracking-widest mb-2">
                <Mic className="w-4 h-4" />
                <span>IIAB 2027 Speaker &amp; Panelist Hub</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black tracking-tight">
                {eventCard.speakerSession?.topicTitle || 'Keynote & Technical Conference Track'}
              </h3>
              <p className="text-xs text-slate-300 mt-1">
                Upload your official speaker headshot photo, presentation slide deck (.pptx / .pdf), and confirm your stage AV requirements ahead of your session at Jio World Convention Centre, Pavilion 3.
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-white/10 text-xs">
                <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Assigned Stage / Hall</span>
                  <span className="font-bold text-white mt-0.5 block">
                    {eventCard.speakerSession?.stageOrHall || 'Pavilion 3 · 128 PAX Conference Hall'}
                  </span>
                </div>
                <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Session Date</span>
                  <span className="font-bold text-white mt-0.5 block">
                    {eventCard.speakerSession?.sessionDate || '27–28 July 2027'}
                  </span>
                </div>
                <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Time Slot</span>
                  <span className="font-bold text-white mt-0.5 block">
                    {eventCard.speakerSession?.sessionStartTime || 'To Be Announced by Track Chair'}
                  </span>
                </div>
              </div>
            </div>

            {/* Speaker Headshot + Slide Deck Upload + AV Requirements Form */}
            <form
              onSubmit={handleSaveSpeakerMaterials}
              className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-sm space-y-6"
            >
              {speakerSaveSuccess && (
                <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{speakerSaveSuccess}</span>
                </div>
              )}

              {speakerSaveError && (
                <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{speakerSaveError}</span>
                </div>
              )}

              {/* 0. Official Speaker Headshot Photo Upload */}
              <div className="space-y-3 pb-5 border-b border-slate-100">
                <label className="block text-sm font-bold text-slate-900">
                  Official Speaker Headshot Photo (.jpg, .png, .webp)
                </label>
                <p className="text-xs text-slate-500">
                  Displayed on the public 2-Day Conference Agenda, Speaker Directory, and your printed 4×6 VIP Speaker Badge.
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-200 border border-slate-300 shrink-0 flex items-center justify-center">
                    {headshotUrl ? (
                      <img src={headshotUrl} alt={attendee.fullName} className="w-full h-full object-cover object-top" />
                    ) : (
                      <Camera className="w-7 h-7 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2.5 w-full">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-semibold cursor-pointer shadow-2xs transition-colors">
                        <Camera className="w-3.5 h-3.5" />
                        <span>{uploadingHeadshot ? 'Uploading Photo...' : 'Upload Headshot Photo'}</span>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={handleHeadshotFileSelect}
                          disabled={uploadingHeadshot}
                          className="hidden"
                        />
                      </label>
                      <span className="text-[11px] text-slate-500">or paste a direct photo URL below:</span>
                    </div>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={headshotUrl}
                      onChange={(e) => setHeadshotUrl(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>
              </div>

              {/* 1. Slide Deck Upload */}
              <div className="space-y-3">
                <label className="block text-sm font-bold text-slate-900">
                  1. Presentation Slide Deck (.pptx or .pdf, max 50 MB)
                </label>
                <p className="text-xs text-slate-500">
                  16:9 widescreen format recommended. Uploaded files are pre-loaded onto the Pavilion 3 AV control desk laptop prior to your session.
                </p>

                <div className="border-2 border-dashed border-slate-300 hover:border-[#0A3D27] rounded-2xl p-6 text-center transition-colors bg-slate-50/60">
                  <Upload className="w-8 h-8 text-[#0A3D27] mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-800">
                    {uploadingDeck
                      ? 'Uploading presentation to secure storage...'
                      : deckFileName
                      ? `Selected: ${deckFileName}`
                      : 'Select your .pptx or .pdf slide deck to upload'}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 mb-3">
                    Supports Microsoft PowerPoint (.pptx, .ppt) and PDF (.pdf) up to 50 MB
                  </p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-semibold cursor-pointer shadow-xs transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    <span>{uploadingDeck ? 'Uploading...' : 'Choose Slide Deck File'}</span>
                    <input
                      type="file"
                      accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      onChange={handleDeckFileSelect}
                      disabled={uploadingDeck}
                      className="hidden"
                    />
                  </label>
                </div>

                {deckUrl && (
                  <div className="p-3.5 rounded-xl bg-emerald-50/80 border border-emerald-200 flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="font-semibold text-emerald-950 truncate">
                        Active Deck: {deckFileName || deckUrl}
                      </span>
                    </div>
                    <a
                      href={deckUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-emerald-700 hover:text-emerald-900 font-bold shrink-0 inline-flex items-center gap-1"
                    >
                      <span>View Link</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Cloud Link Fallback */}
                <div className="pt-2">
                  <label className="block text-xs font-semibold uppercase text-slate-600 mb-1.5">
                    Or Paste Google Slides / OneDrive / Dropbox Presentation Link
                  </label>
                  <div className="relative">
                    <Link2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="url"
                      placeholder="https://docs.google.com/presentation/d/..."
                      value={cloudDeckLink}
                      onChange={(e) => setCloudDeckLink(e.target.value)}
                      className="w-full border border-slate-300 rounded-xl pl-10 pr-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>
              </div>

              {/* 2. AV & Stage Checklist */}
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <label className="block text-sm font-bold text-slate-900">
                  2. Stage &amp; Audio-Visual (AV) Requirements
                </label>
                <p className="text-xs text-slate-500">
                  Select all equipment and stage setup items needed for your presentation:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {AV_REQUIREMENT_OPTIONS.map((item) => {
                    const checked = avRequirements.includes(item);
                    return (
                      <label
                        key={item}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-medium cursor-pointer transition-all ${
                          checked
                            ? 'bg-emerald-50/70 border-emerald-600 text-emerald-950 font-semibold'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAvRequirement(item)}
                          className="w-4 h-4 accent-[#0A3D27] rounded"
                        />
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* 3. Special Notes for Stage Manager */}
              <div className="space-y-2 pt-4 border-t border-slate-100">
                <label className="block text-sm font-bold text-slate-900">
                  3. Special Instructions for Stage &amp; AV Crew
                </label>
                <textarea
                  rows={3}
                  placeholder="Any video cues, live product demos, panel seating preferences, or pronunciation notes for the session emcee..."
                  value={speakerNotes}
                  onChange={(e) => setSpeakerNotes(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={savingSpeakerMaterials}
                  className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-5 rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-50"
                >
                  {savingSpeakerMaterials
                    ? 'Saving Speaker Materials...'
                    : 'Save Presentation & AV Preferences'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyIiabPage;
